/**
 * MorePage — the shared shell for the secondary destinations reached from the
 * left-nav "More" menu (blocked users, blog, saved posts, tracker, awards,
 * leader board, AI buddies). Gives each page the same back bar and spacing.
 */
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { ChevronLeftIcon } from '../../components/ui/icons';
import { resolveBackDestination } from './backNavigation';
import './more.css';

interface MorePageProps {
  title: string;
  subtitle?: string;
  backFallbackPath?: string;
  /** Rendered at the end of the top bar (filters, primary action). */
  action?: ReactNode;
  children: ReactNode;
}

export function MorePage({
  title,
  subtitle,
  backFallbackPath = '/',
  action,
  children,
}: MorePageProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <section className="more-page" aria-label={title}>
      <div className="more-page__topbar">
        <button
          type="button"
          className="more-page__back"
          aria-label={t('common.buttons.back')}
          onClick={() => {
            const destination = resolveBackDestination(
              window.history.state,
              backFallbackPath,
            );
            if (destination.to === -1) {
              navigate(-1);
              return;
            }
            navigate(destination.to, { replace: destination.replace });
          }}
        >
          <ChevronLeftIcon size={20} />
        </button>
        <div className="more-page__heading">
          <h1 className="more-page__title">{title}</h1>
          {subtitle && <p className="more-page__subtitle">{subtitle}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}
